/** @jsxImportSource @emotion/react */
import { EmptyContent, Header } from '@quickbit/backoffice-core-frontend';
import {
  createStyles,
  Icon,
  Typography,
  UtilityButton,
  useTheme,
} from '@quickbit/qb-design-system';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetMessages, useRunAgent } from 'api/useAgentChatApi';
import { MessageInput } from './MessageInput';
import { MessageList } from './MessageList';

export const ChatSession = () => {
  const styles = useStyles();
  const { palette, spacing } = useTheme();
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();

  const { data, isLoading, isError, refetch } = useGetMessages(sessionId || '');

  const runAgentMutation = useRunAgent(sessionId || '');

  const handleSendMessage = (message: string) => {
    runAgentMutation.mutate({ message });
  };

  if (isError) {
    return (
      <EmptyContent
        heading="Oops! Something went wrong."
        body="Error loading chat session"
        action={
          <UtilityButton
            text="Try again"
            variant="primary"
            onClick={() => void refetch()}
          />
        }
      />
    );
  }

  return (
    <div css={styles.wrapper}>
      <Header>
        <div css={styles.titleContainer}>
          <div css={styles.back} onClick={() => navigate('/agent-chat')}>
            <Typography variant="subtitle" color={palette.grey.x300}>
              Agent Chat
            </Typography>
          </div>
          <div css={styles.chevron}>
            <Icon name="chevron" size={spacing.xxs} />
          </div>
          <Typography variant="subtitle">Chat Session</Typography>
        </div>
      </Header>
      <div css={styles.chatContainer}>
        <MessageList
          messages={data?.messages || []}
          isLoading={isLoading}
          isPending={runAgentMutation.isPending}
        />
        <MessageInput
          onSend={handleSendMessage}
          disabled={runAgentMutation.isPending}
        />
      </div>
    </div>
  );
};

const useStyles = createStyles(({ spacing, palette }) => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  back: {
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
      textDecorationColor: palette.grey.x300,
    },
  },
  chevron: {
    transform: 'rotate(180deg)',
    padding: `0 ${spacing.xxs}`,
    svg: {
      fill: 'none',
      stroke: palette.grey.x300,
    },
  },
  chatContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    flex: 1,
    minHeight: 0,
    border: '1px solid #e0e0e0',
    borderRadius: spacing.xxxs,
    overflow: 'hidden',
  },
}));
